import * as React from 'react';
import {createContext, useContext, useRef} from 'react';
import {
  createViewConfig,
  view,
  provider,
  assignProps,
  dynamic,
} from '@sail/engine';

import type {IconAsset} from '@sail/icons/types';
import type {
  ItemTransforms,
  ListCollectionComponent,
  SectionTransforms,
} from '@sail/react-aria';
import {
  CollectionProvider,
  PressResponderContext,
  useId,
  useItem,
  useListCollection,
} from '@sail/react-aria';
import {tokens as $} from '@sail/tokens';

import {css} from '../css';
import type {View} from '../view';
import {Action, ActionConfig, type ActionProps} from '../components/Action';
import {Icon, IconConfig} from '../components/Icon';
import {createComposite, rovingTabIndex} from '../components/rovingTabIndex';

const paddingVar = 'var(--s-list-padding)';
const sizeCalc = `calc(100% + ${paddingVar} * 2)`;

export type ListItemProps = View.IntrinsicElement<
  'div',
  {
    /**
     * Unique identifier
     */
    id: string;
    /**
     * Title of the list item, displayed as a heading above the description.
     */
    title?: React.ReactNode;
    /**
     * Secondary text content of the list item, displayed as a paragraph below the title.
     */
    description?: React.ReactNode;
    /**
     * Icon that appears before the title and description.
     */
    icon?: IconAsset | React.ReactNode;
    /**
     * Content to appear after the title and description, typically representing a
     * value for the list item, but may be used for actions to be taken on the item.
     */
    trailingContent?: React.ReactNode;
    /**
     * Event handler called when the item is pressed
     */
    onPress?: ActionProps['onPress'];
    /**
     * The href to navigate to when the item is pressed
     */
    href?: ActionProps['href'];
    /**
     * Where to display the linked URL
     */
    target?: ActionProps['target'];
    /**
     * Whether the press action is disabled
     */
    disabled?: boolean;
    /**
     * The relationship of the linked URL
     */
    rel?: ActionProps['rel'];
    children?: React.ReactNode;

    subviews?: View.Subviews<{
      action: typeof Action;
      icon: typeof Icon | 'div';
      heading: 'div';
      title: 'div';
      description: 'div';
      trailingContent: 'div';
      children: 'div';
    }>;
  }
>;

interface ListContextValue {
  onAction?: (id: string) => void;
}

const ListContext = createContext<ListContextValue>({});

const reactNodeAsText = (node: React.ReactNode): string => {
  if (typeof node === 'string') {
    return node;
  }
  if (React.isValidElement(node)) {
    return reactNodeAsText(React.Children.toArray(node.props.children));
  }
  if (Array.isArray(node)) {
    return node.map(reactNodeAsText).join('');
  }
  return '';
};

const ListItemTransforms: ItemTransforms<ListItemProps, 'title'> = {
  defaultField: 'title',
  makeGenericItem(props: ListItemProps) {
    const {title, description} = props;
    const textValue = reactNodeAsText(title) || reactNodeAsText(description);
    return {
      'aria-label': props['aria-label'] || textValue,
      textValue,
      data: {},
    };
  },
};

export const ListItemConfig = createViewConfig({
  name: 'ListItem',
  props: {} as ListItemProps,
});

const LIST_ITEM_CLASS = 'sail-beta-list-item';

const hoverNextSiblingItemStyles = css.global({
  [`.${LIST_ITEM_CLASS}:is([data-is-clickable]):not([data-is-disabled]):hover + .${LIST_ITEM_CLASS}:is([data-is-clickable])`]:
    {
      borderTopColor: 'transparent!important',
    },
});

const hoverStyles = [
  css({
    // We want borders to be visible except when the item is hovered
    // where we want to hide borders above and below the element.
    // To achieve that, we insert a top border for every item except the first one,
    // and on hover, we hide the border on the current and next item.
    transition: `border-color ${$['animation.duration.short']} ${$['animation.easing']}`,

    ':before': {
      content: '""',
      transition: `background-color ${$['animation.duration.short']} ${$['animation.easing']}`,
      borderRadius: 'medium',
      height: sizeCalc,
      width: sizeCalc,
      bleed: paddingVar,
    },
    ':hover': {
      borderTopColor: 'transparent',
      borderBottomColor: 'transparent',
      '--listitem-hover-icon-keyline': $['color.border'],
    },
    ':hover::before': {
      backgroundColor: 'offset',
    },
  }),
  css({
    ':before': {
      gridArea: '1 / 1 / -1 / -1',
    },
  }),
];

const disabledStyles = css({
  opacity: '0.7',
});

export const ListItem = ListItemConfig.createView(
  (props) => {
    const {
      trailingContent,
      icon,
      title,
      description,
      href,
      onPress,
      target,
      rel,
      subviews,
      'aria-disabled': ariaDisabled,
      disabled,
      ...restProps
    } = props;
    const {onAction: onListAction} = useContext(ListContext);
    const pressContext = useContext(PressResponderContext);
    // check whether the list item is used as a trigger of any `PressResponder` ancestor
    const isInsidePressResponder = pressContext != null;
    const clickable = href || onPress || onListAction || isInsidePressResponder;

    // fix: explicitly not using the key from `useItem` here as it's not guaranteed to be unique *on the page*
    // since it will use the `props.id` value as the key when provided.
    const accessibilityId = useId();

    useItem(props.id, ListItemTransforms.makeGenericItem(props));

    const linkRef = useRef<HTMLAnchorElement>(null);
    const clickableProps = {
      href,
      target,
      rel,
      // remove from tab sequence (should still be available to screen readers through landmark roles)
      tabIndex: -1,
      'aria-labelledby': accessibilityId,
      onPress,
      'aria-disabled': ariaDisabled,
      disabled,
      uses: [
        !disabled &&
          onListAction &&
          assignProps({onClick: () => onListAction(props.id)}),
        rovingTabIndex,
        disabled && css({cursor: 'default'}),
      ],
    };

    const isContained = Boolean(title && description);

    return (
      <view.div
        {...restProps}
        role="listitem"
        css={{alignY: 'baseline'}}
        uses={[
          hoverNextSiblingItemStyles,
          ...(clickable && !disabled ? hoverStyles : []),
          ...(disabled ? [disabledStyles] : []),
          assignProps({className: LIST_ITEM_CLASS}),
        ]}
        data-is-clickable={clickable ? 'true' : undefined}
        data-is-disabled={disabled ? 'true' : undefined}
      >
        {/* It's important that this Action be first in the source order
        so that any subsequent links/buttons/actions are position above this one (https://inclusive-components.design/cards/#theauthorlink) */}
        {clickable ? (
          <Action
            {...clickableProps}
            inherits={subviews.action}
            ref={linkRef}
            aria-labelledby={accessibilityId}
          />
        ) : null}

        {icon ? (
          <IconConfig.Customize
            variants={{
              appearance: {
                contained: {
                  css: {
                    alignSelfY: 'top',
                    // absorb some of the extra grid gap when the icon is contained
                    bleedRight: 'xsmall',
                    keyline: 'var(--listitem-hover-icon-keyline, initial)',
                  },
                },
              },
            }}
          >
            {'svg' in (icon as IconAsset) ? (
              <Icon
                icon={icon as IconAsset}
                inherits={subviews.icon as View.Subview<IconAsset>}
                appearance={isContained ? 'contained' : undefined}
                size={
                  // eslint-disable-next-line no-nested-ternary
                  isContained
                    ? 'small'
                    : description || props.children
                    ? 'xxsmall'
                    : 'xsmall'
                }
                css={{
                  transition: `box-shadow ${$['animation.duration.short']} ${$['animation.easing']}`,
                  pointerEvents: 'none',
                }}
              />
            ) : (
              <view.div
                css={{
                  alignSelfY: 'top',
                  bleedRight: 'xsmall',
                  pointerEvents: 'none',
                }}
                inherits={
                  subviews.icon as View.Subview<View.IntrinsicElement<'div'>>
                }
              >
                {icon as React.ReactNode}
              </view.div>
            )}
          </IconConfig.Customize>
        ) : null}
        <view.div inherits={subviews.heading}>
          <view.div
            id={accessibilityId}
            inherits={subviews.title}
            css={[
              description || props.children
                ? css({font: 'label.medium.emphasized'})
                : css({font: 'label.medium'}),
            ]}
          >
            {title}
          </view.div>
          {description ? (
            <view.div inherits={subviews.description}>{description}</view.div>
          ) : null}
        </view.div>
        {trailingContent ? (
          <view.div inherits={subviews.trailingContent}>
            {trailingContent}
          </view.div>
        ) : null}
        {props.children ? (
          <view.div inherits={subviews.children}>{props.children}</view.div>
        ) : null}
      </view.div>
    );
  },
  {
    css: {
      position: 'relative',
      font: 'label.medium',
      paddingY: 'small',
      // this token is likely to be aliased as `border.subdued` in the future
      borderColor: $['color.neutral.50'],
      borderTopStyle: 'solid',
      borderTopWidth: 'small',
      stack: 'z',
      gapX: 'space.150',
      gapY: 'small',
      ':first-child': {
        borderTopWidth: '0',
      },
      ':last-child': {
        borderBottomStyle: 'solid',
        borderBottomWidth: 'small',
      },
    },

    uses: [
      dynamic(({icon, children}) => {
        return [
          css({
            display: 'grid',
            // the icon is on a row with with the heading (title and description) and the trailing content
            // and finally the children content forms a second row aligned with the heading.
            // the "action" spans the whole width and height of the item.
            gridTemplateAreas: icon
              ? `"icon heading trailing"`
              : `"heading trailing"`,
            gridTemplateColumns: icon ? `min-content 1fr auto` : `1fr auto`,
            // conditionally add a second row if there is children content
            gridTemplateRows: children ? 'auto auto' : 'auto',
          }),
        ];
      }),
    ],

    subviews: {
      icon: {
        css: {
          color: 'subdued',
        },
        // Icons occupy the first column, and span all rows
        uses: [css({gridColumn: '1 / 1', gridRow: '1 / -1'})],
      },
      heading: {
        uses: [css({gridArea: 'heading'})],
        css: {overflow: 'hidden', stack: 'y', gap: 'xxsmall'},
      },
      trailingContent: {
        css: {
          fontVariantNumeric: 'tabular-nums',
          color: 'subdued',
        },
        uses: [
          css({
            gridArea: 'trailing',
            alignSelf: 'center',
            justifySelf: 'end',
          }),
        ],
      },
      description: {
        css: {
          font: 'label.small',
          textColor: 'subdued',
        },
      },
      action: {
        css: {
          height: sizeCalc,
          width: sizeCalc,
          bleed: paddingVar,
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        },
      },
      children: {
        // We want children to sit under both the heading and trailing areas, but not under the icon area
        uses: [css({gridRowStart: '2', gridColumn: 'heading / -1'})],
        css: {
          font: 'label.small',
          textColor: 'subdued',
        },
      },
    },
  },
);

export type ListProps<T> = ListCollectionComponent<T, ListItemProps, 'title'> &
  View.IntrinsicElement<
    'div',
    {
      /**
       * Press event handler which receives the corresponding key of the list item that was pressed
       */
      onAction?: (id: string) => void;
    }
  >;

export const ListConfig = createViewConfig({
  name: 'List',
  props: {} as ListProps<unknown>,
});

const listItemKeyboardNavigation = createComposite({
  orientation: 'vertical',
  wrap: true,
  // only move focus between list items
  accept: (node) => node.parentElement?.getAttribute('role') === 'listitem',
});

export const List = ListConfig.createGenericView(
  <T extends object>({inherits, ...props}: View.RenderProps<ListProps<T>>) => {
    const {children, data, item, onAction, ...rest} = props;
    const collection = useListCollection({
      children,
      data,
      item,
      itemTransforms: ListItemTransforms,
      // TODO: make sections on collection API optional?
      groupTransforms: {
        idField: 'id',
        makeGenericItem: () => ({}),
      } as SectionTransforms<{id: string}, 'id'>,
    });

    return (
      <CollectionProvider collection={collection}>
        <view.div
          role="list"
          inherits={inherits}
          uses={[listItemKeyboardNavigation, provider(ListContext, {onAction})]}
          {...rest}
        >
          {collection.render(
            (props: ListItemProps) => (
              <ListItem key={props.id} {...props} />
            ),
            // "sections" not supported in List
            () => (
              <></>
            ),
          )}
        </view.div>
      </CollectionProvider>
    );
  },
  {
    uses: [
      css({'--s-list-padding': $['space.small']}),
      // This ensures actions are clickable and sit above the clickable layer
      // by forming a new stacking context. A z-index is not required here
      // because the <Action /> comes first in the source order.
      // https://inclusive-components.design/cards/#theauthorlink
      ActionConfig.customize({
        uses: [
          css({
            position: 'relative',
          }),
        ],
      }),
    ],
  },
);
